import React, { useEffect, useState } from 'react';
import { adminApi, fmtCurrency, fmtDate } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { StatusPill } from '@/components/fintech/StatusPill';
import { CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export function AdminRequestsPage({ kind }) {
  const endpoint = kind === 'deposits' ? '/admin/deposits' : '/admin/withdrawals';
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('PENDING');
  const [rejectId, setRejectId] = useState(null);
  const [reason, setReason] = useState('');

  const load = () => adminApi.get(endpoint, { params: status !== 'all' ? { status } : {} }).then(r => setItems(r.data.items || []));
  useEffect(() => { load(); }, [status]);

  const approve = async (id) => { try { await adminApi.post(`${endpoint}/${id}/approve`); toast.success('Approved'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const doReject = async () => { try { await adminApi.post(`${endpoint}/${rejectId}/reject`, { reason }); toast.success('Rejected'); setRejectId(null); setReason(''); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))] capitalize">{kind}</h1>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-48"><SelectValue /></SelectTrigger><SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="PENDING">Pending</SelectItem>
          <SelectItem value="APPROVED">Approved</SelectItem>
          <SelectItem value="REJECTED">Rejected</SelectItem>
        </SelectContent></Select>
      </div>
      <Card className="bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--secondary))]"><tr>
            <th className="p-3 text-left">Date</th>
            <th className="p-3 text-left">User</th>
            <th className="p-3 text-left">Currency</th>
            <th className="p-3 text-right">Amount</th>
            {kind === 'withdrawals' && <th className="p-3 text-left">Recipient</th>}
            <th className="p-3 text-left">Status</th>
            <th className="p-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t">
                <td className="p-3 text-xs">{fmtDate(it.created_at)}</td>
                <td className="p-3">{it.user?.email}<div className="text-xs text-[hsl(var(--muted-foreground))]">{it.user?.first_name} {it.user?.last_name}</div></td>
                <td className="p-3">{it.currency}</td>
                <td className="p-3 text-right tabular-nums">{fmtCurrency(it.amount, it.currency)}</td>
                {kind === 'withdrawals' && <td className="p-3 text-xs">{it.details?.recipient_name}<div className="text-[hsl(var(--muted-foreground))]">{it.details?.recipient_account}</div></td>}
                <td className="p-3"><StatusPill status={it.status} /></td>
                <td className="p-3 text-right">
                  {it.status === 'PENDING' && (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" onClick={() => approve(it.id)} className="bg-[hsl(152_45%_34%)]" data-testid={`approve-${it.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                      <Button size="sm" variant="destructive" onClick={() => setRejectId(it.id)} data-testid={`reject-${it.id}`}><XCircle className="h-4 w-4" /></Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No requests</div>}
      </Card>
      <Dialog open={!!rejectId} onOpenChange={o => !o && setRejectId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject request</DialogTitle></DialogHeader>
          <Textarea placeholder="Reason" value={reason} onChange={e => setReason(e.target.value)} rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doReject}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminDepositsPage() { return <AdminRequestsPage kind="deposits" />; }
